import React from 'react';
import { Home, BookOpen, MessageCircle, CalendarDays, Plus } from 'lucide-react';
import PropTypes from 'prop-types';
import GlassTabBar, { TAB_BADGE_CLASSES } from './GlassTabBar.jsx';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { SESSION_STATUS } from '../../constants';
import { haptic } from '../../utils/haptics';

/**
 * Bottom-Navigation der Haupt-App.
 *
 * Das Glas-Material, die Kapsel-Geometrie und der Tab-Baustein liegen seit
 * September 2026 in `ui/GlassTabBar.jsx` — das Dashboard nutzt dieselbe Leiste
 * (`dashboards/DashboardBottomNav.js`), und zwei Kopien waeren zwei Hoehen
 * gewesen, waehrend `--bottom-nav-clearance` nur eine kennt.
 *
 * Oktober 2026 (Polishing iOS 1.0.3):
 * - Sessions zeigen `CalendarDays` statt `Users`. „Users“ steht in der App für
 *   Personen; ein Symbol hat genau eine Bedeutung (TOKENS.md, Icon-Tabelle).
 * - „Läuft“ pulsiert nicht mehr. Eine Endlosschleife im Randblick zieht bei
 *   jedem Blick auf die Leiste Aufmerksamkeit, obwohl nichts Neues passiert ist
 *   (CorporateDesign §7).
 * - Zähler und Punkte stehen in Tinte (`TAB_BADGE_CLASSES`).
 */

const Navigation = ({
  currentView,
  setCurrentView,
  activeSession,
  onSessionClick,
  onStartSession
}) => {
  const { unreadCounts } = useNotificationContext();

  const isMessagesActive = currentView === 'chat' || currentView === 'chat-overview';
  const hasActiveSession = activeSession?.status === SESSION_STATUS.ACTIVE;

  // Haptik nach Bedeutung (Oktober 2026): Ein Reiterwechsel ist ein
  // Wertwechsel (`selection`), und ein Tipp auf den schon aktiven Reiter
  // bleibt stumm. „Neu“ und „Läuft“ starten eine Aktion (`light`). Vorher
  // gab jeder Tipp einen leichten Impuls, die Mitte sogar einen mittleren.
  // Optional verkettet, weil Tests `utils/haptics` oft nur teilweise mocken.
  const withTabHaptic = (fn, active) => () => {
    if (!active) haptic?.selection?.();
    fn();
  };
  const withActionHaptic = (fn) => () => {
    haptic?.light?.();
    fn();
  };
  const homeActive = currentView === 'home';
  const journalActive = currentView === 'journal';
  const sessionsActive = currentView === 'sessions';

  const items = [
    {
      key: 'home',
      icon: Home,
      label: 'Start',
      active: homeActive,
      onClick: withTabHaptic(() => setCurrentView('home'), homeActive),
      ariaLabel: 'Startseite'
    },
    {
      key: 'journal',
      icon: BookOpen,
      label: 'Logbuch',
      active: journalActive,
      onClick: withTabHaptic(() => setCurrentView('journal'), journalActive),
      ariaLabel: 'Lernlogbuch'
    },
    // Mittleres Ziel: laufende Session oder "Neue Session"
    hasActiveSession
      ? {
        key: 'session',
        label: 'Läuft',
        active: true,
        onClick: withActionHaptic(onSessionClick),
        ariaLabel: 'Aktive Lernsession',
        // Ein ruhiger Punkt statt eines gefüllten Lucide-Kreises mit
        // pulsierendem Halo: gleiche Größe, gleiche Farbe, keine Schleife.
        children: (
          <span className="relative h-[22px] w-[22px] flex items-center justify-center" aria-hidden="true">
            <span className="h-4 w-4 rounded-full bg-primary" />
          </span>
        )
      }
      : {
        key: 'session',
        label: 'Neu',
        onClick: withActionHaptic(onStartSession),
        ariaLabel: 'Neue Lernsession erstellen',
        // Gefuellter Kreis statt umrandetem Quadrat: Das Rahmen-Quadrat war
        // das iOS-7-Idiom fuer "Erstellen"; in iOS 26 ist die Primaeraktion
        // eine gefuellte, getoente Linse. Groesse 22px wie die Symbole daneben,
        // damit die Beschriftungen auf einer Linie bleiben.
        children: (
          <span className="relative h-[22px] w-[22px] flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-token-sm transition-transform duration-fast active:scale-95">
            <Plus className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          </span>
        )
      },
    {
      key: 'chat',
      icon: MessageCircle,
      label: 'Chats',
      active: isMessagesActive,
      onClick: withTabHaptic(() => setCurrentView('chat-overview'), isMessagesActive),
      ariaLabel: `Nachrichten${unreadCounts.chat > 0 ? ` (${unreadCounts.chat} ungelesen)` : ''}`,
      badge: unreadCounts.chat > 0 && (
        <span
          className={`absolute top-0.5 right-2 h-4 min-w-[16px] px-1 text-2xs font-semibold leading-none rounded-full flex items-center justify-center ${TAB_BADGE_CLASSES}`}
          aria-hidden="true"
        >
          {unreadCounts.chat > 9 ? '9+' : unreadCounts.chat}
        </span>
      )
    },
    {
      key: 'sessions',
      icon: CalendarDays,
      label: 'Sessions',
      active: sessionsActive,
      onClick: withTabHaptic(() => setCurrentView('sessions'), sessionsActive),
      ariaLabel: `Lernsessions${unreadCounts.sessions > 0 ? ' (neue Aktivität)' : ''}`,
      badge: unreadCounts.sessions > 0 && (
        <span
          className={`absolute top-1 right-2.5 h-2 w-2 rounded-full ${TAB_BADGE_CLASSES}`}
          aria-hidden="true"
        />
      )
    }
  ];

  return (
    // data-bottom-nav ist ein Messpunkt: Die Runden-Ansicht (MatchRoundView)
    // richtet die Hoehe des Kartenstapels an der Oberkante dieses Elements aus,
    // statt eine Leistenhoehe zu schaetzen. Das <nav> hat bewusst kein
    // padding-top — seine Oberkante IST die Oberkante der Glas-Kapsel. Wer hier
    // etwas umbaut, pruefe MatchRoundView gegen. Die Dashboard-Leiste traegt das
    // Attribut absichtlich NICHT: `querySelector` nimmt den ersten Treffer.
    // `rail`: Ab Tablet wandert dieselbe Kapsel an den linken Rand. Der
    // Messpunkt oben bleibt davon unberuehrt — MatchRoundView prueft, ob die
    // Leiste UNTERHALB des Kartenstapels liegt (`navTop > top`). Als Spalte
    // links liegt sie das nicht, der Zweig faellt absichtlich durch, und die
    // Feed-Hoehe kommt dann aus `.match-feed { height: auto }` (index.css).
    <GlassTabBar rail items={items} ariaLabel="Hauptnavigation" navProps={{ 'data-bottom-nav': true }} />
  );
};

Navigation.propTypes = {
  currentView: PropTypes.string.isRequired,
  setCurrentView: PropTypes.func.isRequired,
  activeSession: PropTypes.object,
  onSessionClick: PropTypes.func.isRequired,
  onStartSession: PropTypes.func.isRequired
};

export default Navigation;
